/* ────────────────────────────────────────────────────────────────
   Kushface — Home scroll choreography
   Loads GSAP + ScrollTrigger from CDN, wires reveals + pinned sections.
   Respects prefers-reduced-motion (degrades to in-place reveals).
   ──────────────────────────────────────────────────────────────── */

(function () {
  "use strict";

  var reducedMotion = window.matchMedia &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  function loadScript(src) {
    return new Promise(function (resolve, reject) {
      var s = document.createElement("script");
      s.src = src;
      s.async = true;
      s.onload = resolve;
      s.onerror = reject;
      document.head.appendChild(s);
    });
  }

  function instantReveal() {
    document.querySelectorAll("[data-scroll-reveal]").forEach(function (el) {
      el.classList.add("is-revealed");
    });
    document.querySelectorAll(".kf-grid__cell").forEach(function (el) {
      el.classList.add("is-revealed");
    });
  }

  if (reducedMotion) {
    instantReveal();
    return;
  }

  Promise.all([
    loadScript("https://cdn.jsdelivr.net/npm/gsap@3.13.0/dist/gsap.min.js"),
    loadScript("https://cdn.jsdelivr.net/npm/gsap@3.13.0/dist/ScrollTrigger.min.js")
  ]).then(function () {
    if (!window.gsap || !window.ScrollTrigger) return instantReveal();
    var gsap = window.gsap;
    gsap.registerPlugin(window.ScrollTrigger);

    // ── Simple reveal on enter ────────────────────────────────────
    document.querySelectorAll("[data-scroll-reveal]").forEach(function (el) {
      gsap.to(el, {
        opacity: 1,
        y: 0,
        duration: 0.9,
        ease: "power2.out",
        scrollTrigger: { trigger: el, start: "top 80%", once: true,
          onEnter: function () { el.classList.add("is-revealed"); }
        }
      });
    });

    // ── Scroll-scrubbed rise on the statement headline ────────────
    // Stronger than a one-shot fade — the headline tracks scroll position so
    // it "lifts" continuously as the section moves through the viewport.
    document.querySelectorAll(".kf-statement").forEach(function (section) {
      var headline = section.querySelector(".kf-statement__text");
      if (!headline) return;
      gsap.fromTo(headline,
        { y: 140, opacity: 0 },
        {
          y: 0,
          opacity: 1,
          ease: "none",
          scrollTrigger: {
            trigger: section,
            start: "top bottom",
            end: "center center",
            scrub: 0.6
          }
        }
      );
    });

    // ── Product grid stagger reveal ───────────────────────────────
    var gridCells = gsap.utils.toArray("[data-scroll-reveal-grid] .kf-grid__cell");
    if (gridCells.length) {
      ScrollTrigger.batch(gridCells, {
        start: "top 85%",
        onEnter: function (batch) {
          gsap.to(batch, {
            opacity: 1,
            y: 0,
            duration: 0.8,
            ease: "power2.out",
            stagger: 0.15,
            onComplete: function () {
              batch.forEach(function (el) { el.classList.add("is-revealed"); });
            }
          });
        }
      });
    }

    // ── HERO scroll-scrubbed frame sequence ───────────────────────
    document.querySelectorAll("[data-scroll-scrub-hero]").forEach(function (hero) {
      var frame = hero.querySelector("[data-scroll-frame-sequence]");
      var frameData = hero.querySelector("[data-scroll-frame-sequence-srcs]");
      var heroTarget = hero.querySelector("[data-scroll-target]");

      if (frame && frameData) {
        var frameSrcs = [];
        var currentFrame = 0;
        try {
          frameSrcs = JSON.parse(frameData.textContent || "[]");
        } catch (e) {
          frameSrcs = [];
        }

        // Preload AND retain every frame for the page lifetime. Throwaway
        // Image() objects get GC'd on memory-constrained phones, so the
        // scrubber's `frame.src = …` then re-fetches/re-decodes mid-scroll
        // — the stutter that reads as "slow on mobile". Holding the
        // references keeps them decoded so the scrub stays smooth.
        //
        // ALSO call img.decode() on each preloaded Image. On iOS Safari,
        // setting an <img>.src to a URL whose bytes are cached still
        // triggers a fresh main-thread decode (5-15ms per frame). Calling
        // .decode() up front forces the bitmap into the browser's image
        // cache so the subsequent `frame.src = url` hits the cached
        // bitmap and renders in ~1ms instead of re-decoding from disk.
        var frameCache = [];
        frameSrcs.forEach(function (src) {
          var img = new Image();
          if ("decoding" in img) img.decoding = "async";
          img.src = src;
          if (typeof img.decode === "function") {
            img.decode().catch(function () { /* decode can reject on broken images — ignore */ });
          }
          frameCache.push(img);
        });

        var FRAME_MS = 1000 / 36;
        var looping = false;
        var loopRaf = 0;
        var lastTick = 0;
        var step = function (now) {
          if (!looping) return;
          if (!lastTick) lastTick = now;
          if (now - lastTick >= FRAME_MS) {
            lastTick = now - ((now - lastTick) % FRAME_MS);
            currentFrame =
              currentFrame >= frameSrcs.length - 1 ? 0 : currentFrame + 1;
            frame.src = frameSrcs[currentFrame];
          }
          loopRaf = window.requestAnimationFrame(step);
        };
        var startFromScroll = function () {
          if (looping || reducedMotion || !frameSrcs.length || window.pageYOffset <= 0) return;
          looping = true;
          lastTick = 0;
          window.removeEventListener("scroll", startFromScroll);
          loopRaf = window.requestAnimationFrame(step);
        };
        window.addEventListener("scroll", startFromScroll, { passive: true });
      }

      if (heroTarget) heroTarget.setAttribute("aria-hidden", "true");

      // Scroll cue: visible through the hero, hidden once the broadcast
      // bar below it is shown (same trigger the site nav uses to reveal,
      // inverted). Fail open — leave it visible if either is missing.
      var scrollCue = hero.querySelector(".kf-hero__scroll-cue");
      var broadcastBar = document.querySelector(".kf-testcard");
      if (scrollCue && broadcastBar && typeof IntersectionObserver !== "undefined") {
        var cueObserver = new IntersectionObserver(
          function (entries) {
            entries.forEach(function (entry) {
              var barShown =
                entry.isIntersecting || entry.boundingClientRect.bottom < 0;
              scrollCue.classList.toggle("is-gone", barShown);
            });
          },
          { threshold: 0 }
        );
        cueObserver.observe(broadcastBar);
      }
    });

    // ── PILLARS — pinned cross-fade + channel-change flash on advance ──
    var pillarsRoot = document.querySelector("[data-pinned-pillars]");
    if (pillarsRoot) {
      var pillars = pillarsRoot.querySelectorAll(".kf-pillar");
      if (pillars.length > 1) {
        var lastIdx = 0;
        ScrollTrigger.create({
          trigger: pillarsRoot,
          start: "top top",
          // ~1 viewport per pillar (was 3) — tighter, less empty scroll
          end: "+=" + ((pillars.length - 1) * 100) + "%",
          pin: ".kf-pillars__sticky",
          scrub: 1,
          onUpdate: function (self) {
            var idx = Math.min(
              pillars.length - 1,
              Math.floor(self.progress * pillars.length)
            );
            if (idx !== lastIdx) {
              lastIdx = idx;
              if (typeof window.kfBroadcastFlash === "function") {
                window.kfBroadcastFlash();
              }
            }
            pillars.forEach(function (el, i) {
              el.classList.toggle("is-active", i === idx);
            });
          }
        });
      }
    }

    // ── TEST CARD — scroll-linked horizontal tune motion ──────────
    // Mobile uses a 100vw-wide repeating tile (see home-testcard.css /
    // home-scroll.css mobile override) so the scrub is safe to run at
    // every breakpoint — one tile slides off-right, the next slides in.
    //
    // Mobile test_card_1 (the section directly after hero) is pinned to
    // the bottom of the viewport via `position: sticky` for the whole
    // duration of hero + SHC + merch scroll. ScrollTrigger reads the
    // element's natural scroll position (not the visually-pinned one),
    // so the default `end: "bottom top"` fires the moment the testcard's
    // natural box exits the top of the viewport — long before the card
    // visually disappears. Result: bars freeze around when merch shows.
    // Fix: when this testcard is the sticky-pinned mobile instance,
    // extend the scrub end with `+=` (relative to start) so bars keep
    // tracking scroll for the full duration the card stays visually
    // pinned. Desktop and test_card_3 keep the original card-relative
    // trigger — they aren't sticky and only need to scrub during transit.
    var isMobile =
      window.matchMedia && window.matchMedia("(max-width: 768px)").matches;
    document.querySelectorAll(".kf-testcard").forEach(function (card) {
      var bars = card.querySelector(".kf-testcard__bars");
      if (!bars) return;
      var rev = card.hasAttribute("data-reverse");
      var section = card.closest(".kf-testcard-section");
      var prevSection = section && section.previousElementSibling;
      var isStickyPinned =
        isMobile &&
        prevSection &&
        prevSection.classList.contains("kf-hero-section");
      gsap.fromTo(bars,
        { backgroundPositionX: rev ? "18vw" : "-18vw" },
        {
          backgroundPositionX: rev ? "-18vw" : "18vw",
          ease: "none",
          scrollTrigger: {
            trigger: card,
            start: "top bottom",
            // Sticky-pinned mobile card: 400% of viewport height of extra
            // runway from start, covers hero + SHC + merch + a buffer.
            // Default card: scrub through its own viewport transit.
            end: isStickyPinned ? "+=400%" : "bottom top",
            scrub: 1
          }
        }
      );
    });

    // Refresh after fonts / images settle to avoid stale measurements.
    window.addEventListener("load", function () { ScrollTrigger.refresh(); });
  }).catch(function () {
    // CDN failed — degrade gracefully.
    instantReveal();
  });

  // Mobile test_card_1 pin: handled in CSS via `position: sticky` on
  // `.kf-hero-section + .kf-testcard-section` (see home-scroll.css).
})();
